import React from "react";
import { ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { CurrencyInr, Coins, ChartLineUp, Sparkle } from "@phosphor-icons/react";
import { Slider } from "../components/ui/slider";
import { fmtINR, fmtPct } from "../lib/kostra";

const COLORS = ["#0A3B24", "#D4AF37", "#7A9E5C"];

function StatCard({ label, value, sub, icon: Icon, accent = "#0A3B24", testId }) {
  return (
    <div className="kostra-card p-5 group" data-testid={testId}>
      <div className="flex items-start justify-between">
        <span className="kostra-data-label">{label}</span>
        <div
          className="h-9 w-9 rounded-md flex items-center justify-center"
          style={{ background: `${accent}10`, color: accent }}
        >
          <Icon size={18} weight="duotone" />
        </div>
      </div>
      <div className="mt-3 kostra-numeric-xl text-3xl text-[#111827]">
        {value}
      </div>
      {sub && <div className="mt-1 text-xs text-[#4B5563]">{sub}</div>}
    </div>
  );
}

export default function SummaryDashboard({ summary, recipe, onChangeFoodCostPct }) {
  const breakdown = [
    { name: "Raw Materials", value: summary.raw_total },
    { name: "Variable", value: summary.variable_total },
    { name: "Fixed", value: summary.fixed_total },
  ].filter((d) => d.value > 0);

  const priceComparison = [
    { tier: "Min (35%)", price: summary.min_price },
    { tier: "Recommended (30%)", price: summary.recommended_price },
    { tier: "Premium (25%)", price: summary.premium_price },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Total Recipe Cost"
          value={fmtINR(summary.total_recipe_cost)}
          sub={`Across ${recipe.num_portions || 1} portions`}
          icon={CurrencyInr}
          testId="stat-total-recipe-cost"
        />
        <StatCard
          label="Cost / Portion"
          value={fmtINR(summary.cost_per_portion)}
          sub="Production cost"
          icon={Coins}
          accent="#D4AF37"
          testId="stat-cost-per-portion"
        />
        <StatCard
          label="Suggested Price"
          value={fmtINR(summary.suggested_price)}
          sub={`@ ${fmtPct(summary.desired_food_cost_pct)} food cost`}
          icon={Sparkle}
          accent="#0A3B24"
          testId="stat-suggested-price"
        />
        <StatCard
          label="Profit / Portion"
          value={fmtINR(summary.profit_per_portion)}
          sub={`Gross margin ${fmtPct(summary.gross_margin_pct)}`}
          icon={ChartLineUp}
          accent="#16A34A"
          testId="stat-profit"
        />
      </div>

      {summary.net_weight_per_portion_g > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="net-weight-strip">
          <StatCard
            label="Net Weight / Portion"
            value={`${summary.net_weight_per_portion_g.toFixed(1)} g`}
            sub="As set in Dish Info"
            icon={Coins}
            accent="#0A3B24"
            testId="stat-net-weight-portion"
          />
          <StatCard
            label="Total Net Weight"
            value={`${(summary.total_net_weight_g / 1000).toFixed(3)} kg`}
            sub={`${recipe.num_portions || 1} portions × ${summary.net_weight_per_portion_g.toFixed(1)} g`}
            icon={Coins}
            accent="#D4AF37"
            testId="stat-total-net-weight"
          />
          <StatCard
            label="Cost / Kg"
            value={fmtINR(summary.cost_per_kg)}
            sub="Production cost per kilo"
            icon={CurrencyInr}
            accent="#0A3B24"
            testId="stat-cost-per-kg"
          />
          <StatCard
            label="Cost / Gram"
            value={`₹${(summary.cost_per_gram || 0).toFixed(4)}`}
            sub="Useful for plating + bulk"
            icon={CurrencyInr}
            accent="#7A9E5C"
            testId="stat-cost-per-gram"
          />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="kostra-card p-5 md:p-6 lg:col-span-1" data-testid="cost-breakdown-chart">
          <h3 className="kostra-section-title text-lg">Cost Breakdown</h3>
          <p className="text-xs text-[#4B5563] mt-1 mb-3">Where your rupee goes per recipe</p>
          {breakdown.length ? (
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={breakdown} dataKey="value" innerRadius={45} outerRadius={75} paddingAngle={2}>
                    {breakdown.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v) => fmtINR(v)} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-56 flex items-center justify-center text-sm text-[#4B5563]">
              Add ingredients to see the breakdown.
            </div>
          )}
          <div className="mt-2 space-y-1">
            {breakdown.map((b, i) => (
              <div key={b.name} className="flex items-center justify-between text-xs">
                <span className="inline-flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />
                  {b.name}
                </span>
                <span className="font-mono-num">{fmtINR(b.value)}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="kostra-card p-5 md:p-6 lg:col-span-2" data-testid="price-tiers-chart">
          <div className="flex items-end justify-between gap-4 mb-2">
            <div>
              <h3 className="kostra-section-title text-lg">Market Competitive Pricing</h3>
              <p className="text-xs text-[#4B5563] mt-1">Suggested tiers benchmarked against typical Swiggy/Zomato food cost ratios</p>
            </div>
            <div className="text-right">
              <div className="kostra-data-label">Recommended</div>
              <div className="kostra-numeric-xl text-2xl text-[#0A3B24]">
                {fmtINR(summary.recommended_price)}
              </div>
            </div>
          </div>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={priceComparison} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
                <XAxis dataKey="tier" tick={{ fontSize: 12, fill: "#4B5563" }} />
                <YAxis tick={{ fontSize: 11, fill: "#4B5563" }} tickFormatter={(v) => `₹${v}`} />
                <Tooltip formatter={(v) => fmtINR(v)} />
                <Bar dataKey="price" radius={[6, 6, 0, 0]} fill="#0A3B24">
                  {priceComparison.map((_, i) => (
                    <Cell key={i} fill={i === 1 ? "#D4AF37" : "#0A3B24"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="kostra-card p-5 md:p-6" data-testid="food-cost-pct-card">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h3 className="kostra-section-title text-lg">Desired Food Cost %</h3>
            <p className="text-xs text-[#4B5563] mt-1">
              Adjust the ratio to instantly recalculate the Suggested Selling Price.
            </p>
          </div>
          <div className="text-right">
            <span className="kostra-numeric-xl text-3xl text-[#0A3B24]">
              {fmtPct(summary.desired_food_cost_pct)}
            </span>
          </div>
        </div>
        <div className="mt-4">
          <Slider
            value={[summary.desired_food_cost_pct || 30]}
            min={10}
            max={60}
            step={1}
            onValueChange={(v) => onChangeFoodCostPct(v[0])}
            data-testid="food-cost-pct-slider"
          />
          <div className="flex justify-between text-[10px] text-[#4B5563] mt-1">
            <span>10%</span><span>20%</span><span>30%</span><span>40%</span><span>50%</span><span>60%</span>
          </div>
        </div>
      </div>
    </div>
  );
}
